import React, { useState, useCallback, useEffect } from 'react';
import type { ToggleFieldFormData, ToggleFieldFormLabels, ToggleFieldFormProps, Category } from '../../shared/types/toggleFieldForm';
import { SCORE_COLORS } from './IconPicker';

export type { ToggleFieldFormData, ToggleFieldFormLabels, ToggleFieldFormProps };

export function ToggleFieldForm({
  initialData,
  onSubmit,
  onCancel,
  onIconPickerOpen,
  errors = {},
  labels: l,
  mode,
  categories = [],
  showCategorySelect = false,
  additionalActivityName,
  isSubmitting = false,
}: ToggleFieldFormProps) {
  const [formData, setFormData] = useState<ToggleFieldFormData>(initialData);

  // Sync icon/score from props - parent owns these via the icon picker
  useEffect(() => {
    setFormData(prev => ({ ...prev, icon: initialData.icon, score: initialData.score }));
  }, [initialData.icon, initialData.score]);

  const handleNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, name: e.target.value }));
  }, []);

  const handleCategoryChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setFormData(prev => ({ ...prev, categoryId: e.target.value }));
  }, []);

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  }, [formData, onSubmit]);

  // Update form data when icon/score changes from parent
  const updateIconAndScore = useCallback((icon: string, score: number) => {
    setFormData(prev => ({ ...prev, icon, score }));
  }, []);

  // Expose updateIconAndScore to parent via ref or callback
  // For now, we'll handle this through the container's state management

  const backgroundColor = SCORE_COLORS[formData.score] ?? SCORE_COLORS[100];

  return (
    <form onSubmit={handleSubmit}>
      {/* Error messages */}
      {Object.keys(errors).length > 0 && (
        <div className="alert alert-danger mb-3">
          <ul className="mb-0">
            {Object.entries(errors).flatMap(([field, messages]) =>
              messages.map((msg, i) => (
                <li key={`${field}-${i}`}>{msg}</li>
              ))
            )}
          </ul>
        </div>
      )}

      {/* Converting from additional activity alert */}
      {additionalActivityName && (
        <div className="alert alert-info mb-3" role="alert">
          <i className="bi bi-info-circle me-2"></i>
          <strong>{l.convertingFromActivity}</strong>
          <p className="mb-0 mt-2">{l.convertingFromActivityHelp}</p>
        </div>
      )}

      {/* Icon picker trigger + Name input */}
      <div className="form-group row mb-2">
        <div className="col-12">
          {/* Icon picker trigger button */}
          <button
            type="button"
            className="btn p-0 me-2 d-inline-block"
            onClick={onIconPickerOpen}
            style={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              backgroundColor,
              border: '2px solid rgba(0,0,0,0.1)',
              cursor: 'pointer',
              verticalAlign: 'top',
            }}
            data-testid="icon-picker-trigger"
          >
            <i
              className={`icon-flex icon-${formData.icon}`}
              style={{ width: 40, height: 40 }}
            />
          </button>

          {/* Name input */}
          <div className="form-floating d-inline-block" style={{ width: 'calc(100% - 80px)', verticalAlign: 'top' }}>
            <input
              type="text"
              className="form-control"
              id="toggle-field-name"
              value={formData.name}
              onChange={handleNameChange}
              required
              autoFocus={mode === 'new' && !additionalActivityName}
              disabled={!!additionalActivityName}
              placeholder={l.name}
            />
            <label htmlFor="toggle-field-name" className="form-label">{l.name}</label>
          </div>
        </div>
      </div>

      {/* Category select (conditional) */}
      {showCategorySelect && (
        <div className="form-group mb-3">
          <div className="form-floating">
            <select
              className="form-select"
              id="toggle-field-category"
              value={formData.categoryId || ''}
              onChange={handleCategoryChange}
              required
              data-testid="category-select"
            >
              <option value="">— {l.selectCategory} —</option>
              {categories.map((cat: Category) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
            <label htmlFor="toggle-field-category" className="form-label">{l.category}</label>
          </div>
        </div>
      )}

      {/* Form Buttons */}
      <div className="form-group d-flex justify-content-end buttons-wrapper">
        <button
          type="button"
          className="btn btn-link me-3"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          {l.cancel}
        </button>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={isSubmitting}
          data-testid="save-button"
        >
          {isSubmitting ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              {l.save}
            </>
          ) : (
            l.save
          )}
        </button>
      </div>
    </form>
  );
}

export default ToggleFieldForm;
